import { useEffect, useMemo, useState } from 'react';
import { BsSearch, BsThreeDotsVertical } from 'react-icons/bs';
import { FaChevronRight } from 'react-icons/fa';
import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Flex as ChakraFlex,
  IconButton as ChakraIconButton,
  MenuButton as ChakraMenuButton,
} from '@chakra-ui/react';
import { Drawer } from '../../../components/UI-CHAKRA/Drawer';
import { DrawerOverlay } from '../../../components/UI-CHAKRA/DrawerOverlay';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Input } from '../../../components/UI-CHAKRA/Input/Input';
import { InputGroup } from '../../../components/UI-CHAKRA/Input/InputGroup';
import { InputRightElement } from '../../../components/UI-CHAKRA/Input/InputRightElement';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import { Menu } from '../../../components/UI-CHAKRA/Menu/Menu';
import { MenuItem } from '../../../components/UI-CHAKRA/Menu/MenuItem';
import { MenuList } from '../../../components/UI-CHAKRA/Menu/MenuList';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useDragToScroll } from '../../../helpers/useDragToScroll';
import { Model, ModelService } from '../services';
import { FILTER_TYPES, useModelStore } from '../../../stores/modelStore';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { canManageModel } from './modelPermissions';
import { FeedbackContext } from '../../../providers/FeedbackProvider';
import { useCallback, useContext } from 'react';
import { Tracker } from '../../../shared/tracker/Tracker';
import { logger } from '../../../helpers/logger';
import { extractPatientAge } from '../../../helpers/patientAge';
import { useDebouncedValue, SEARCH_DEBOUNCE_MS } from '../../../helpers/useDebouncedValue';
import { toast } from '../../../helpers/toast';
import { ApplyModelDrawer } from './ApplyModelDrawer';
import { EditModelDrawer } from './EditModelDrawer';
import { ModelActionSheet } from './PrescriptionModelCard';
import { useModelFilterPills } from './modelFilterPills';

interface SelectModelDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModelCategory = 'sugestao' | 'institucional' | 'meusModelos';

const CATEGORY_LABEL: Record<ModelCategory, string> = {
  sugestao: 'Sugestão',
  institucional: 'Institucional',
  meusModelos: 'Meus modelos',
};

function getModelCategory(model: Model): ModelCategory {
  if (model.sponsored) return 'sugestao';
  if (model.origin === 'institutional') return 'institucional';
  return 'meusModelos';
}

export function SelectModelDrawer(props: SelectModelDrawerProps) {
  const { isOpen, onClose } = props;
  const isMobile = useIsMobile();
  const pillsDrag = useDragToScroll<HTMLDivElement>();

  const fetchModels = useModelStore((store) => store.fetchModels);
  const setFeedback = useModelStore((store) => store.setFeedback);
  const models = useModelStore((store) => store.models);
  const sponsoredModels = useModelStore((store) => store.sponsoredModels);
  const loadMoreModels = useModelStore((store) => store.loadMoreModels);
  const loadingMoreModels = useModelStore((store) => store.loadingMoreModels);
  const modelsError = useModelStore((store) => store.modelsError);
  /**
   * Se alguma listagem já foi pedida. Zero significa que nada foi solicitado ainda.
   *
   * Vale para a linha de pills e para o corpo da lista: sem isso um cold open do drawer
   * (`models` vazio, nenhuma requisição disparada) renderiza "Nenhum modelo encontrado" antes
   * de haver resposta, em vez do skeleton.
   */
  const modelsRequested = useModelStore((store) => store.modelsRequestId > 0);
  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const { askForConfirmation } = useContext(FeedbackContext);

  const [activeModel, setActiveModel] = useState<Model | null>(null);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [filter, setFilter] = useState<FILTER_TYPES>(FILTER_TYPES.ALL);
  const searchFilter = useModelStore((store) => store.searchFilter);
  const setSearchFilter = useModelStore((store) => store.setSearchFilter);

  useEffect(() => {
    // Fechar o drawer descarta pill e busca. A busca virou estado do store (é ela que define o
    // `name` enviado ao backend), então precisa ser limpa aqui — senão reabrir o drawer mostra
    // a lista ainda filtrada pelo termo da sessão anterior.
    if (!isOpen) {
      setFilter(FILTER_TYPES.ALL);
      setSearchFilter('');
    }
  }, [isOpen, setSearchFilter]);

  const mergedModels = useMemo(() => {
    const existingIds = new Set(models.map((model) => model.id));
    return [...models, ...sponsoredModels.filter((model) => !existingIds.has(model.id))];
  }, [models, sponsoredModels]);

  const searchedModels = useMemo(() => {
    const search = searchFilter.trim().toLowerCase();
    if (!search) return mergedModels;
    return mergedModels.filter((model) => model.name.toLowerCase().includes(search));
  }, [mergedModels, searchFilter]);

  // As pills ignoram a busca de propósito: quem as define é a lista carregada sem termo.
  const pillOptions = useModelFilterPills(models, sponsoredModels);

  // O filtro é estado local do drawer e sobrevive a mudanças na lista. Se a pill selecionada
  // deixa de existir, cair para "Todos" evita a lista vazia sem nenhuma pill marcada.
  useEffect(() => {
    if (filter === FILTER_TYPES.ALL) return;
    if (pillOptions.some((option) => option.filter === filter)) return;
    setFilter(FILTER_TYPES.ALL);
  }, [pillOptions, filter]);

  const visibleModels = useMemo(() => {
    const byCategory = searchedModels.filter((model) => {
      const isSuggestion = model.sponsored;
      const isInstitutional = model.origin === 'institutional';
      switch (filter) {
        case FILTER_TYPES.HIGHLIGHTED:
          return isSuggestion;
        case FILTER_TYPES.INSTITUTIONAL:
          return !isSuggestion && isInstitutional;
        case FILTER_TYPES.PERSONAL:
          return !isSuggestion && !isInstitutional;
        default:
          return true;
      }
    });

    const categoryOrder = (model: Model): number => {
      if (model.sponsored) return 0;
      if (model.origin === 'institutional') return 1;
      return 2;
    };

    return byCategory.sort((a, b) => {
      const orderDiff = categoryOrder(a) - categoryOrder(b);
      if (orderDiff !== 0) return orderDiff;
      return (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR', { sensitivity: 'base' });
    });
  }, [searchedModels, filter]);

  const searchTerm = searchFilter.trim();
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  /**
   * Busca a lista quando o termo muda e, na primeira vez, quando o `partnerId` fica
   * disponível.
   *
   * `fetchModels` aborta sem carregar nada enquanto o `prescriberConfiguration` não trouxer
   * `idParceiro` — e sem marcar erro, porque é um estado transitório. Só que o termo de busca
   * também não muda nesse intervalo, então sem o `modelsRequestId` no guard a lista ficaria
   * vazia para sempre, exibida como "nenhum modelo". O contador só passa de zero quando uma
   * busca de fato começou, o que o torna o sinal de "ainda não carregou".
   */
  useEffect(() => {
    if (!isOpen || !prescription) return;
    const { modelsRequestId, modelsName } = useModelStore.getState();
    const neverLoaded = modelsRequestId === 0;
    if (!neverLoaded && (debouncedSearch || undefined) === modelsName) return;
    fetchModels(
      prescription.Prescritor.idPrescritor,
      prescription.idPrescricao,
      extractPatientAge(prescription.Paciente?.Idade),
      debouncedSearch || undefined,
    ).catch((error) => logger.error('Error searching models:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearch, prescription?.idPrescricao, prescriberConfiguration?.idParceiro]);

  const refreshModels = useCallback(async () => {
    if (!prescription) return;
    try {
      await fetchModels(
        prescription.Prescritor.idPrescritor,
        prescription.idPrescricao,
        extractPatientAge(prescription.Paciente?.Idade),
        searchTerm || undefined,
        { force: true },
      );
    } catch (error) {
      logger.error('Error fetching models:', error);
    }
  }, [prescription, fetchModels, searchTerm]);

  const onDeleteModel = useCallback(
    async (model: Model) => {
      const confirmation = await askForConfirmation({
        text: 'O modelo selecionado será excluído permanentemente.',
        confirmText: 'Deletar modelo',
        cancelText: 'Cancelar',
        title: 'Deletar modelo salvo',
      });

      if (!confirmation) return;

      const doctorId = prescription?.Prescritor?.idPrescritor;
      const partnerId = prescriberConfiguration?.idParceiro;
      const prescriptionId = prescription?.idPrescricao;
      if (!doctorId || !partnerId || !prescriptionId) {
        toast.error('Não foi possível deletar o modelo, tente novamente.');
        return;
      }

      try {
        await ModelService.deleteModel(model.id, doctorId, partnerId, prescriptionId);
        await refreshModels();
        Tracker.trackEvent({
          event: 'Model Deleted',
          parameters: { deletedModel: model.name },
        });
        setFeedback({ type: 'removed', modelName: model.name });
      } catch (e) {
        logger.error(e);
        toast.error('Não foi possível deletar o modelo, tente novamente.');
      }
    },
    [askForConfirmation, refreshModels, setFeedback, prescription, prescriberConfiguration]
  );

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!prescription) return;
      const el = e.currentTarget;
      const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      if (reachedBottom) {
        loadMoreModels(prescription.Prescritor.idPrescritor, prescription.idPrescricao);
      }
    },
    [prescription, loadMoreModels]
  );

  return (
    <>
      {/*
        * `blockScrollOnMount={false}`: ao escolher um modelo, o drawer de aplicação abre por
        * cima e os dois ficam montados. O lock de scroll deste aqui cancela o `wheel` dentro do
        * de cima, e a lista de itens do modelo fica sem rolagem. O lock não faz falta: o
        * conteúdo atrás nunca rola, o `#App` é `overflow: hidden`.
        */}
      <Drawer isOpen={isOpen} placement={'right'} onClose={onClose} size={'md'} blockScrollOnMount={false}>
        <DrawerOverlay bg={'rgba(0, 0, 0, 0.4)'} />
        <DrawerContent maxW={{ base: '100%', md: '445px' }} data-cy={'selecionar-modelo-drawer'}>
          <DrawerHeader
            display={'flex'}
            alignItems={'center'}
            h={'72px'}
            px={'24px'}
            py={'24px'}
            fontSize={'18px'}
            fontWeight={600}
            color={'#1E252B'}
            borderBottom={'1px solid #DEE2E6'}
          >
            Adicionar modelo à prescrição
          </DrawerHeader>
          <DrawerCloseButton top={'24px'} right={'24px'} color={'#1E252B'} />

          <DrawerBody px={'16px'} py={'16px'} overflowY={'auto'} onScroll={handleListScroll}>
            <InputGroup h={'40px'} mb={'16px'}>
              <Input
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder={'Busque por nome do modelo'}
                h={'40px'}
                fontSize={'14px'}
                borderRadius={'10px'}
                borderColor={'#CBBFE6'}
                data-testid={'selecionar-modelo-search-input'}
              />
              <InputRightElement h={'40px'} pointerEvents={'none'}>
                <BsSearch size={16} color={'#868E96'} />
              </InputRightElement>
            </InputGroup>

            {modelsRequested && (
              <ChakraFlex
                ref={pillsDrag.ref}
                {...pillsDrag.handlers}
                gap={'8px'}
                flexWrap={'nowrap'}
                overflowX={'auto'}
                mb={'16px'}
                w={'100%'}
                minW={0}
                cursor={'grab'}
                _active={{ cursor: 'grabbing' }}
                css={{
                  '::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  WebkitOverflowScrolling: 'touch',
                  userSelect: 'none',
                  touchAction: 'pan-x',
                }}
              >
                {pillOptions.map((option) => {
                  const isActive = filter === option.filter;
                  return (
                    <Box
                      key={option.filter}
                      as={'button'}
                      onClick={() => setFilter(option.filter)}
                      px={'16px'}
                      h={'32px'}
                      flexShrink={0}
                      whiteSpace={'nowrap'}
                      borderRadius={'20px'}
                      border={'1px solid'}
                      borderColor={isActive ? 'primary.main' : '#DEE2E6'}
                      bg={isActive ? 'primary.main' : 'white'}
                      color={isActive ? 'white' : '#1E252B'}
                      fontSize={'14px'}
                      fontWeight={500}
                      cursor={'pointer'}
                      transition={'all 0.15s ease'}
                      _hover={{ bg: isActive ? 'primary.main' : 'gray.lightest' }}
                      data-testid={`selecionar-modelo-pill-${option.filter}`}
                    >
                      {option.label}
                    </Box>
                  );
                })}
              </ChakraFlex>
            )}

            {!modelsRequested ? (
              <ModelsRowsSkeleton />
            ) : modelsError ? (
              <Flex direction={'column'} alignItems={'center'} gap={'8px'} py={'32px'}>
                <Text fontSize={'14px'} color={'#868E96'} textAlign={'center'}>
                  Não foi possível carregar os modelos.
                </Text>
                <Box
                  as={'button'}
                  onClick={refreshModels}
                  bg={'transparent'}
                  border={'none'}
                  cursor={'pointer'}
                  p={'4px 8px'}
                  data-testid={'selecionar-modelo-error-retry'}
                >
                  <Text fontSize={'14px'} fontWeight={500} color={'primary.main'} textDecoration={'underline'}>
                    Tentar novamente
                  </Text>
                </Box>
              </Flex>
            ) : visibleModels.length === 0 ? (
              <Flex justifyContent={'center'} alignItems={'center'} py={'32px'}>
                <Text fontSize={'14px'} color={'#868E96'}>
                  Nenhum modelo encontrado.
                </Text>
              </Flex>
            ) : (
              <Flex direction={'column'}>
                {visibleModels.map((model) => (
                  <ModelRow
                    key={model.id}
                    model={model}
                    isMobile={isMobile}
                    onSelect={() => setActiveModel(model)}
                    onEdit={() => setEditingModel(model)}
                    onDelete={() => onDeleteModel(model)}
                  />
                ))}
              </Flex>
            )}

            {loadingMoreModels && (
              <Flex justifyContent={'center'} py={'16px'}>
                <Spinner thickness={'2px'} color={'primary.main'} size={'sm'} />
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/*
        Aplicar o modelo fecha também esta listagem: a prescrição já recebeu os itens, e deixar
        a lista aberta por cima obrigaria o prescritor a fechá-la para ver o resultado. Cancelar
        usa só o `onClose` e devolve o prescritor à lista, onde ele estava.
      */}
      <ApplyModelDrawer
        isOpen={Boolean(activeModel)}
        model={activeModel}
        onApplied={onClose}
        onClose={() => setActiveModel(null)}
      />
      <EditModelDrawer
        isOpen={Boolean(editingModel)}
        model={editingModel}
        onClose={() => setEditingModel(null)}
      />
    </>
  );
}

interface ModelRowProps {
  model: Model;
  isMobile: boolean | undefined;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Placeholder da lista enquanto a listagem não foi pedida.
 *
 * Espelha a estrutura de `ModelRow` — etiqueta de categoria sobre o nome — para a transição
 * para os dados reais não deslocar o conteúdo.
 */
function ModelsRowsSkeleton() {
  return (
    <Flex direction={'column'} data-testid={'selecionar-modelo-skeleton'}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Flex
          key={index}
          direction={'column'}
          gap={'6px'}
          px={'8px'}
          py={'12px'}
          borderBottom={'1px solid #F1F3F5'}
          css={{
            '@keyframes modelRowSkeletonPulse': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.6 },
            },
            animation: 'modelRowSkeletonPulse 1.4s ease-in-out infinite',
          }}
        >
          <Box h={'12px'} w={'72px'} bg={'#F1F3F5'} borderRadius={'4px'} />
          <Box h={'14px'} w={'60%'} bg={'#F1F3F5'} borderRadius={'4px'} />
        </Flex>
      ))}
    </Flex>
  );
}

function ModelRow(props: ModelRowProps) {
  const { model, isMobile, onSelect, onEdit, onDelete } = props;
  const category = getModelCategory(model);
  const isInstitutionalModelAdmin = useModelStore((store) => store.isInstitutionalModelAdmin);
  const showActions = canManageModel(model, isInstitutionalModelAdmin);
  const [actionSheetOpen, setActionSheetOpen] = useState(false);

  const handleDeleteFromSheet = () => {
    setActionSheetOpen(false);
    onDelete();
  };
  const handleEditFromSheet = () => {
    setActionSheetOpen(false);
    onEdit();
  };

  return (
    <>
    <Flex
      alignItems={'center'}
      gap={'8px'}
      px={'8px'}
      py={'12px'}
      borderBottom={'1px solid #F1F3F5'}
      cursor={'pointer'}
      transition={'background 0.15s ease'}
      _hover={{ bg: 'gray.lightest' }}
      onClick={onSelect}
      data-cy={model.name}
      data-testid={'selecionar-modelo-row'}
    >
      <Flex direction={'column'} gap={'2px'} flex={1} minW={0}>
        <Text fontSize={'12px'} fontWeight={600} color={'#868E96'}>
          {CATEGORY_LABEL[category]}
        </Text>
        <Text
          fontSize={'14px'}
          fontWeight={500}
          color={'#1E252B'}
          lineHeight={'1.3'}
          overflow={'hidden'}
          textOverflow={'ellipsis'}
          whiteSpace={'nowrap'}
        >
          {model.name}
        </Text>
      </Flex>

      {showActions && (
        <Box onClick={(e) => e.stopPropagation()}>
          {isMobile ? (
            <ChakraIconButton
              aria-label={'Ações do modelo'}
              icon={<BsThreeDotsVertical size={16} color={'#1E252B'} />}
              onClick={() => setActionSheetOpen(true)}
              variant={'ghost'}
              size={'xs'}
              w={'24px'}
              h={'24px'}
              minW={'24px'}
              borderRadius={'4px'}
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              data-testid={'selecionar-modelo-actions-trigger'}
            />
          ) : (
            <Menu placement={'bottom-end'} autoSelect={false} isLazy>
              <ChakraMenuButton
                as={ChakraIconButton}
                aria-label={'Ações do modelo'}
                icon={<BsThreeDotsVertical size={16} color={'#1E252B'} />}
                variant={'ghost'}
                size={'xs'}
                w={'24px'}
                h={'24px'}
                minW={'24px'}
                borderRadius={'4px'}
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
              />
              <MenuList minW={'120px'} py={'4px'}>
                <MenuItem fontSize={'14px'} onClick={onEdit} data-testid={'selecionar-modelo-action-edit'}>
                  Editar
                </MenuItem>
                <MenuItem
                  fontSize={'14px'}
                  color={'error.main'}
                  onClick={onDelete}
                  data-testid={'selecionar-modelo-action-delete'}
                >
                  Remover
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Box>
      )}

      <Box color={'#1E252B'} flexShrink={0}>
        <FaChevronRight size={12} />
      </Box>
    </Flex>

    {isMobile && showActions && (
      <ModelActionSheet
        isOpen={actionSheetOpen}
        modelName={model.name}
        showEdit
        showDelete
        onEdit={handleEditFromSheet}
        onDelete={handleDeleteFromSheet}
        onClose={() => setActionSheetOpen(false)}
      />
    )}
    </>
  );
}
